import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, TrendingUp, FileText, Clock } from 'lucide-react';
import { ContentDisplayService } from '@/services/ContentDisplayService';

interface EnhancementStatsProps {
  courseId?: string;
}

export const EnhancementStatsWidget = ({ courseId }: EnhancementStatsProps) => {
  const [stats, setStats] = useState({
    totalModules: 0,
    enhancedModules: 0,
    avgWordCount: 0,
    recentlyEnhanced: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await ContentDisplayService.getGlobalEnhancementStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to load enhancement stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [courseId]);

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const enhancementRate = stats.totalModules > 0 
    ? Math.round((stats.enhancedModules / stats.totalModules) * 100) 
    : 0;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Content Enhancement Status
        </CardTitle>
        <CardDescription>
          Real-time statistics from database
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Total Modules</span>
            </div>
            <p className="text-2xl font-bold">{stats.totalModules}</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              <span className="text-sm text-muted-foreground">Enhanced</span>
            </div>
            <p className="text-2xl font-bold text-success">{stats.enhancedModules}</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">Avg Words</span>
            </div>
            <p className="text-2xl font-bold">{stats.avgWordCount}</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-warning" />
              <span className="text-sm text-muted-foreground">Last 24h</span>
            </div>
            <p className="text-2xl font-bold">{stats.recentlyEnhanced}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Enhancement Rate</span>
            <Badge variant={enhancementRate >= 80 ? 'default' : 'secondary'}>
              {enhancementRate}%
            </Badge>
          </div>
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${enhancementRate}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
